import { Dialog } from '@angular/cdk/dialog';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { AnalyticsService, POPOUT_EVENTS } from '@qtek/libs/analytics';
import { PopoverService } from '@qtek/libs/popover';
import { TranslateCorePipe } from '@qtek/libs/translation-core';
import { PhonePipe } from '@qtek/libs/ui';
import { POPOUT_MODAL_DATA } from '@qtek/shared/models';
import { ShortNumberPipe } from '@qtek/shared/pipes';
import {
  POPOUT_DIALOG_PARENT_BARRIER,
  POPOUT_OVERLAY_PROVIDERS,
} from './popout-overlay';

export const POPOUT_WINDOW = new InjectionToken<Window>('POPOUT_WINDOW');

@Injectable({ providedIn: 'root' })
export class PopoutService {
  private readonly injector = inject(EnvironmentInjector);
  /** SD-3048: parent of every popout injector, so popout dialogs don't join the root's. */
  private readonly dialogParentBarrier = createEnvironmentInjector(
    POPOUT_DIALOG_PARENT_BARRIER,
    this.injector
  );
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly parentDocument: Document = inject(DOCUMENT);
  private readonly analytics: AnalyticsService = inject(AnalyticsService);

  private windowInstances = new Map<string, Window>();

  openPopoutModal(
    component: Type<any>,
    data: any,
    windowName = 'ChildPopout',
    viewContainerRef?: ViewContainerRef,
    windowFeatures = 'width=1024,height=768,resizable'
  ): void {
    let popoutUrl = '/standalone/table.html';
    if (data?.dashboard) {
      windowFeatures = 'width=750,height=400,resizable';
      popoutUrl = '/standalone/dash.html';
    } else if (data?.popoutType === 'ai-agent') {
      popoutUrl = '/standalone/ai-agent.html';
      if (data?.chatId) {
        const params = new URLSearchParams({ chatId: data.chatId });
        popoutUrl += `?${params.toString()}`;
      }
    }

    const windowInstance = window.open(popoutUrl, windowName, windowFeatures);
    if (!windowInstance) {
      return;
    }

    this.windowInstances.set(windowName, windowInstance);
    this.analytics.track(POPOUT_EVENTS.POPOUT_WINDOW_OPENED, {
      windowName,
      popoutType: data?.popoutType ?? (data?.dashboard ? 'dashboard' : 'table'),
    });
    try {
      windowInstance.focus();
    } catch {
      // Ignore focus errors (popup blockers, cross-window issues).
    }

    let initialized = false;
    const initOnce = () => {
      if (initialized) return;
      initialized = true;
      this.initializeNewWindow(
        windowInstance,
        component,
        data,
        viewContainerRef
      );
    };

    const tryInit = () => {
      if (initialized) return;
      if (windowInstance.closed) return;
      const childDoc = windowInstance.document;
      if (!childDoc) return;
      const hostElement = childDoc.getElementById('app-host');
      if (hostElement) {
        initOnce();
      }
    };

    // Handle fast loads (or about:blank -> target) and slower navigations.
    windowInstance.addEventListener('load', tryInit, { once: true });

    const start = Date.now();
    const intervalId = window.setInterval(() => {
      if (initialized) {
        window.clearInterval(intervalId);
        return;
      }

      if (windowInstance.closed) {
        window.clearInterval(intervalId);
        return;
      }

      if (Date.now() - start > 5000) {
        window.clearInterval(intervalId);
        return;
      }

      tryInit();
    }, 50);
  }

  /**
   * SD-3045: brings the popout opened under `windowName` to the front. False when it is not open.
   * Browsers treat focusing another window as a request, which they may not honour.
   */
  focusPopout(windowName: string): boolean {
    const windowInstance = this.windowInstances.get(windowName);
    if (!windowInstance || windowInstance.closed) return false;
    try {
      windowInstance.focus();
    } catch {
      // Ignore focus errors (cross-window issues).
    }
    return true;
  }

  private initializeNewWindow(
    windowInstance: Window,
    component: Type<any>,
    data: any,
    viewContainerRef?: ViewContainerRef
  ): void {
    if (!windowInstance || windowInstance.closed) return;

    const childDoc = windowInstance.document;

    if (data?.dashboard) {
      childDoc.title = 'Dashboard';
    } else if (data?.popoutType === 'ai-agent') {
      childDoc.title = 'Allo';
    } else {
      childDoc.title = 'Table';
    }

    this.copyParentStylesToChild(childDoc);

    const hostElement = childDoc.getElementById('app-host');
    if (!hostElement) {
      console.warn('No #app-host element found in child window');
      return;
    }

    hostElement.innerHTML = '';

    const childInjector = this.createPopoutInjector(
      childDoc,
      data,
      viewContainerRef,
      windowInstance
    );

    const portalOutlet = new DomPortalOutlet(
      hostElement,
      this.appRef,
      childInjector
    );

    // Start watching for dynamically injected styles before attaching.
    this.setupParentStyleMutationObserver(childDoc, windowInstance);
    this.syncParentThemeClasses(childDoc, windowInstance);

    portalOutlet.attach(new ComponentPortal(component, null, childInjector));

    // SD-3045: the component lives in this window's app, so closing or re-opening the popout does
    // not destroy it. Allo's must go with its window: left running, it keeps saving chat state and
    // finishing downloads for a window that is gone. The other popouts stay as they were, because
    // their components disconnect streams on destroy that the page behind them still uses.
    // SD-3048: every popout's dialogs go with its window.
    const isAllo = data?.popoutType === 'ai-agent';
    const onPageHide = () => {
      childInjector.get(Dialog).closeAll();
      if (isAllo) {
        portalOutlet.dispose();
        childInjector.destroy();
      }
    };
    windowInstance.addEventListener('pagehide', onPageHide, { once: true });
  }

  private createPopoutInjector(
    childDoc: Document,
    data: any,
    viewContainerRef?: ViewContainerRef,
    popoutWindow?: Window | null
  ): EnvironmentInjector {
    return createEnvironmentInjector(
      [
        { provide: DOCUMENT, useValue: childDoc },
        { provide: POPOUT_MODAL_DATA, useValue: data },
        ...(popoutWindow
          ? [{ provide: POPOUT_WINDOW, useValue: popoutWindow }]
          : []),
        OverlayContainer,
        Overlay,
        PopoverService,
        // SD-3048: the popout's own Dialog (MatDialog opens every dialog through it) and the
        // overlay services it needs bound to the popout's document. See popout-overlay.ts.
        ...POPOUT_OVERLAY_PROVIDERS,
        TranslateCorePipe,
        ShortNumberPipe,
        PhonePipe,
        ...(viewContainerRef
          ? [{ provide: ViewContainerRef, useValue: viewContainerRef }]
          : []),
      ],
      this.dialogParentBarrier
    );
  }

  private copyParentStylesToChild(childDoc: Document): void {
    const linkEls = Array.from(
      this.parentDocument.querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"]'
      )
    );
    linkEls.forEach(link => {
      const newLink = childDoc.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = link.href;
      childDoc.head.appendChild(newLink);
    });

    const styleBlocks = this.parentDocument.querySelectorAll(
      'style:not([ng-reflect-ng-style])'
    );
    styleBlocks.forEach(parentStyle => {
      const newStyle = childDoc.createElement('style');
      newStyle.textContent = parentStyle.textContent;
      childDoc.head.appendChild(newStyle);
    });

    this.parentDocument.documentElement.classList.forEach(cls => {
      childDoc.documentElement.classList.add(cls);
    });

    this.parentDocument.body.classList.forEach(cls => {
      childDoc.body.classList.add(cls);
    });
  }

  private setupParentStyleMutationObserver(
    childDoc: Document,
    windowInstance: Window
  ): void {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLLinkElement && node.rel === 'stylesheet') {
            const link = childDoc.createElement('link');
            link.rel = 'stylesheet';
            link.href = node.href;
            childDoc.head.appendChild(link);
          } else if (node instanceof HTMLStyleElement) {
            const style = childDoc.createElement('style');
            style.textContent = node.textContent;
            childDoc.head.appendChild(style);
          }
        });
      });
    });

    observer.observe(this.parentDocument.head, {
      childList: true,
      subtree: true,
    });

    const checkInterval = setInterval(() => {
      if (windowInstance.closed) {
        observer.disconnect();
        clearInterval(checkInterval);
      }
    }, 500);
  }

  private syncParentThemeClasses(
    childDoc: Document,
    windowInstance: Window
  ): void {
    const sync = () => {
      childDoc.documentElement.className =
        this.parentDocument.documentElement.className;
      childDoc.body.className = this.parentDocument.body.className;
    };

    sync();

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          sync();
          break;
        }
      }
    });

    observer.observe(this.parentDocument.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    observer.observe(this.parentDocument.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const checkInterval = setInterval(() => {
      if (windowInstance.closed) {
        observer.disconnect();
        clearInterval(checkInterval);
      }
    }, 500);
  }
}
